import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, ApiError } from "../api";
import { ErrorNote, Loading, StatCard, secsToHHMM } from "../components/ui";
import { useLocations, useRoles } from "../hooks";
import type { AttendanceMatrix, EmployeesSummary, PolicyGroup } from "../types";

const monthLabel = (ym: string) => {
  const [y, m] = ym.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString("en", { month: "short", year: "numeric", timeZone: "UTC" });
};

type Mode = "day" | "week" | "month" | "quarter" | "year" | "custom";
type Sort = "present_days" | "total_duration" | "avg_duration" | "avg_arrival" | "avg_exit" | "name";

const iso = (d: Date) => d.toISOString().slice(0, 10);
const nowYM = () => new Date().toISOString().slice(0, 7);

function monthRange(ym: string) {
  const [y, m] = ym.split("-").map(Number);
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return { from: `${ym}-01`, to: `${ym}-${String(last).padStart(2, "0")}` };
}
function shiftYM(ym: string, delta: number) {
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

// --- Week helpers (calendar weeks, Monday–Sunday) ---
function mondayOf(d: Date): string {
  const u = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  u.setUTCDate(u.getUTCDate() - ((u.getUTCDay() + 6) % 7));
  return iso(u);
}
function addDaysIso(isoStr: string, n: number): string {
  const [y, m, d] = isoStr.split("-").map(Number);
  return iso(new Date(Date.UTC(y, m - 1, d + n)));
}
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
function fmtMD(isoStr: string): string {
  const [, m, d] = isoStr.split("-").map(Number);
  return `${MON[m - 1]} ${d}`;
}
function weekLabel(mondayIso: string): string {
  return `${fmtMD(mondayIso)} – ${fmtMD(addDaysIso(mondayIso, 6))}`;
}
function weeksOfMonth(ym: string): string[] {
  const [y, m] = ym.split("-").map(Number);
  const lastOfMonth = new Date(Date.UTC(y, m, 0));
  let cursor = mondayOf(new Date(Date.UTC(y, m - 1, 1)));
  const weeks: string[] = [];
  while (new Date(`${cursor}T00:00:00Z`) <= lastOfMonth) {
    weeks.push(cursor);
    cursor = addDaysIso(cursor, 7);
  }
  return weeks;
}

export default function Employee() {
  const locations = useLocations();
  const [location, setLocation] = useState("");
  const [department, setDepartment] = useState("");
  const [search, setSearch] = useState("");
  const [mode, setMode] = useState<Mode>("month");
  const [month, setMonth] = useState(nowYM());
  const [weekOf, setWeekOf] = useState(mondayOf(new Date()));
  const [year, setYear] = useState(new Date().getFullYear());
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [sort, setSort] = useState<Sort>("present_days");
  const [order, setOrder] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(1);

  const [view, setView] = useState<"list" | "matrix">("list");
  const [data, setData] = useState<EmployeesSummary | null>(null);
  const [matrix, setMatrix] = useState<AttendanceMatrix | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const { isAdmin } = useRoles();
  const [groups, setGroups] = useState<PolicyGroup[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [assignGroup, setAssignGroup] = useState("hybrid");
  const [assignMsg, setAssignMsg] = useState<string | null>(null);

  useEffect(() => {
    if (isAdmin) api.policyGroups().then((r) => setGroups(r.groups)).catch(() => undefined);
  }, [isAdmin]);

  const range = useMemo(() => {
    const end = iso(new Date());
    if (mode === "month") return monthRange(month);
    if (mode === "year") return { from: `${year}-01-01`, to: `${year}-12-31` };
    if (mode === "custom") return { from: from || undefined, to: to || undefined };
    if (mode === "day") return { from: end, to: end };
    if (mode === "week") return { from: weekOf, to: addDaysIso(weekOf, 6) };
    const q0 = new Date(new Date().getFullYear(), Math.floor(new Date().getMonth() / 3) * 3, 1);
    return { from: iso(q0), to: end };
  }, [mode, month, weekOf, year, from, to]);

  const weekOptions = useMemo(() => {
    const list = weeksOfMonth(weekOf.slice(0, 7));
    if (!list.includes(weekOf)) list.push(weekOf);
    return list.sort().map((w) => ({ value: w, label: weekLabel(w) }));
  }, [weekOf]);

  const filters = { location: location || undefined, department: department || undefined,
    search: search || undefined, from: range.from, to: range.to };

  useEffect(() => {
    setLoading(true);
    api.employeesSummary(filters, sort, order, page, 25)
      .then((d) => { setData(d); setError(null); })
      .catch((e: ApiError) => setError(e.message))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, department, search, mode, month, year, from, to, sort, order, page]);

  useEffect(() => {
    if (view !== "matrix") return;
    setLoading(true);
    api.attendanceMatrix(filters)
      .then((m) => { setMatrix(m); setError(null); })
      .catch((e: ApiError) => setError(e.message))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, location, department, search, mode, month, year, from, to]);

  const exportMatrix = async (format: "csv" | "xlsx") => {
    const blob = await api.exportMatrix(filters, format);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `attendance-matrix-${location || "all"}.${format}`; a.click();
    URL.revokeObjectURL(url);
  };

  const reset = () => {
    setLocation(""); setDepartment(""); setSearch(""); setMode("month");
    setMonth(nowYM()); setWeekOf(mondayOf(new Date())); setFrom(""); setTo("");
    setSort("present_days"); setOrder("desc"); setPage(1);
  };

  const toggle = (id: string) => setSelected((s) => {
    const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const toggleAll = (ids: string[], on: boolean) => setSelected((s) => {
    const n = new Set(s); ids.forEach((i) => on ? n.add(i) : n.delete(i)); return n;
  });
  const assignSelected = async () => {
    if (selected.size === 0) { setAssignMsg("Select at least one employee first."); return; }
    try {
      const r = await api.assignBulk([...selected], assignGroup, location || undefined);
      setAssignMsg(`Assigned ${r.assigned} employee(s) to ${assignGroup}.`);
      setSelected(new Set());
      // refetch so the Group column updates
      setData(await api.employeesSummary(filters, sort, order, page, 25));
    } catch (e) { setAssignMsg((e as ApiError).message); }
  };
  const importAssignCsv = async (file: File) => {
    setAssignMsg("Importing CSV…");
    const lines = (await file.text()).split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    const startIdx = /employee/i.test(lines[0]) ? 1 : 0;
    const items = [];
    let bad = 0;
    for (const line of lines.slice(startIdx)) {
      const [emp, grp, loc, name] = line.split(/[,;\t]/).map((x) => x.trim());
      if (!emp || !grp) { bad++; continue; }
      items.push({ employee_id: emp, group_code: grp.toLowerCase(), location: loc || "", employee_name: name || "" });
    }
    try {
      const r = await api.assignBulkItems(items);   // one request — reliable on flaky networks
      setAssignMsg(`CSV import: ${r.assigned} assigned${r.skipped || bad ? `, ${r.skipped + bad} skipped` : ""}.`);
      setData(await api.employeesSummary(filters, sort, order, page, 25));
    } catch (e) { setAssignMsg((e as ApiError).message); }
  };

  const exportCsv = async () => {
    const blob = await api.exportReport({ ...filters, name: search || undefined }, "csv");
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `attendance-${location || "all"}-${range.from}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const th = (key: Sort, label: string) => (
    <th style={{ cursor: "pointer" }} onClick={() => {
      if (sort === key) setOrder(order === "desc" ? "asc" : "desc"); else { setSort(key); setOrder("desc"); }
      setPage(1);
    }}>{label}{sort === key ? (order === "desc" ? " ↓" : " ↑") : ""}</th>
  );

  const k = data?.summary;
  const p = data?.pagination;
  const modes: Mode[] = ["day", "week", "month", "quarter", "year", "custom"];

  return (
    <div>
      <h1 className="h1">Employee Attendance</h1>
      <p className="sub">Filter by location, period and department, browse every employee, and open a full 360° profile.</p>

      <div className="panel">
        <div className="row" style={{ marginBottom: 12 }}>
          {modes.map((m) => (
            <span key={m} className={`chip ${mode === m ? "active" : ""}`}
                  onClick={() => { setMode(m); setPage(1); }} style={{ textTransform: "capitalize" }}>{m}</span>
          ))}
        </div>
        <div className="filters">
          <div className="field">
            <label>Location</label>
            <select value={location} onChange={(e) => { setLocation(e.target.value); setPage(1); }}>
              <option value="">All locations</option>
              {locations.map((l) => <option key={l.code} value={l.code}>{l.name}</option>)}
            </select>
          </div>
          {mode === "week" && (
            <div className="field">
              <label>Week ({MON[Number(weekOf.slice(5, 7)) - 1]})</label>
              <div className="row">
                <button className="ghost" onClick={() => { setWeekOf(addDaysIso(weekOf, -7)); setPage(1); }}>‹</button>
                <select value={weekOf} onChange={(e) => { setWeekOf(e.target.value); setPage(1); }} style={{ minWidth: 150 }}>
                  {weekOptions.map((w) => <option key={w.value} value={w.value}>{w.label}</option>)}
                </select>
                <button className="ghost" onClick={() => { setWeekOf(addDaysIso(weekOf, 7)); setPage(1); }}>›</button>
              </div>
            </div>
          )}
          {mode === "month" && (
            <div className="field">
              <label>Month</label>
              <div className="row">
                <button className="ghost" onClick={() => setMonth(shiftYM(month, -1))}>‹</button>
                <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
                <button className="ghost" onClick={() => setMonth(shiftYM(month, 1))}>›</button>
              </div>
            </div>
          )}
          {mode === "year" && (
            <div className="field">
              <label>Year</label>
              <div className="row">
                <button className="ghost" onClick={() => setYear(year - 1)}>‹</button>
                <input type="number" value={year} style={{ width: 90 }}
                       onChange={(e) => setYear(Number(e.target.value))} />
                <button className="ghost" onClick={() => setYear(year + 1)}>›</button>
              </div>
            </div>
          )}
          {mode === "custom" && (<>
            <div className="field"><label>From</label>
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></div>
            <div className="field"><label>To</label>
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></div>
          </>)}
          <div className="field">
            <label>Department</label>
            <input placeholder="e.g. Employees" value={department}
                   onChange={(e) => { setDepartment(e.target.value); setPage(1); }} />
          </div>
          <div className="field">
            <label>Search (name or ID)</label>
            <input placeholder="e.g. 00000T23 or Mayoori" value={search}
                   onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
          </div>
          <button className="ghost" onClick={reset}>Reset</button>
          <button className="primary" onClick={exportCsv}>Export CSV</button>
        </div>
        <div className="row" style={{ justifyContent: "space-between", marginTop: 4 }}>
          <div className="notice">Showing {range.from ?? "—"} → {range.to ?? "—"}
            {location ? ` · ${location}` : " · all locations"}</div>
          <div className="row">
            <span className={`chip ${view === "list" ? "active" : ""}`} onClick={() => setView("list")}>Employee list</span>
            <span className={`chip ${view === "matrix" ? "active" : ""}`} onClick={() => setView("matrix")}>Month matrix</span>
          </div>
        </div>
      </div>

      {data && k && (
        <div className="cards">
          <StatCard label="Employees present" value={k.employees_present} />
          <StatCard label="Attendance days" value={k.total_attendance_days} />
          <StatCard label="Total inside" value={k.total_inside_hhmm} />
          <StatCard label="Avg daily" value={k.avg_daily_hhmm} />
          <StatCard label="Avg first-in" value={k.avg_arrival ?? "—"} />
          <StatCard label="Avg last-out" value={k.avg_exit ?? "—"} />
          <StatCard label="Incomplete" value={k.incomplete_records} />
        </div>
      )}

      {loading && <Loading what="attendance" />}
      {error && <ErrorNote message={error} />}

      {view === "matrix" && matrix && !loading && (
        <div className="panel">
          <div className="row" style={{ justifyContent: "space-between" }}>
            <h2 style={{ margin: 0 }}>Month-wise attendance (days present)</h2>
            <div className="row">
              <button className="ghost" onClick={() => exportMatrix("csv")}>Export CSV</button>
              <button className="primary" onClick={() => exportMatrix("xlsx")}>Export Excel</button>
            </div>
          </div>
          {matrix.rows.length === 0 ? <div className="empty">No attendance in this range.</div> : (
            <div style={{ overflowX: "auto", marginTop: 10 }}>
              <table className="tabular">
                <thead>
                  <tr>
                    <th>Employee</th><th>ID</th><th>Dept</th>
                    {matrix.months.map((m) => <th key={m}>{monthLabel(m)}</th>)}
                    <th>Grand Total</th>
                  </tr>
                </thead>
                <tbody>
                  {matrix.rows.map((r) => (
                    <tr key={r.employee_id}>
                      <td><Link to={`/employees/${encodeURIComponent(r.employee_id)}`}>{r.employee_name || "—"}</Link></td>
                      <td>{r.employee_id}</td>
                      <td>{r.department}</td>
                      {matrix.months.map((m) => (
                        <td key={m}>{r.counts[m]
                          ? <Link to={`/employees/${encodeURIComponent(r.employee_id)}?month=${m}`}>{r.counts[m]}</Link>
                          : ""}</td>
                      ))}
                      <td><strong>{r.total}</strong></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {view === "list" && data && k && !loading && (
        <>
          <div className="panel">
            <div className="row" style={{ justifyContent: "space-between" }}>
              <h2 style={{ margin: 0 }}>Employees ({p?.total ?? 0})</h2>
              {isAdmin && (
                <div className="row">
                  <span className="notice">Assign work mode:</span>
                  <select value={assignGroup} onChange={(e) => setAssignGroup(e.target.value)}>
                    {groups.map((g) => <option key={g.code} value={g.code}>{g.name}</option>)}
                  </select>
                  <button className="primary" onClick={assignSelected} disabled={selected.size === 0}>
                    Assign {selected.size || ""} selected
                  </button>
                  <label className="ghost" style={{ padding: "9px 12px", borderRadius: 10, cursor: "pointer" }}>
                    Import CSV
                    <input type="file" accept=".csv" style={{ display: "none" }}
                           onChange={(e) => e.target.files?.[0] && importAssignCsv(e.target.files[0])} />
                  </label>
                </div>
              )}
            </div>
            {isAdmin && assignMsg && <div className="notice" style={{ color: "var(--ok)" }}>✓ {assignMsg}</div>}
            {data.items.length === 0 ? <div className="empty">No employees match these filters.</div> : (
              <>
                <div style={{ overflowX: "auto" }}>
                  <table className="tabular">
                    <thead>
                      <tr>
                        {isAdmin && <th><input type="checkbox"
                          checked={data.items.every((r) => selected.has(r.employee_id))}
                          onChange={(e) => toggleAll(data.items.map((r) => r.employee_id), e.target.checked)} /></th>}
                        <th>#</th>
                        {th("name", "Employee")}
                        <th>ID</th><th>Dept</th><th>Location</th><th>Group</th>
                        {th("present_days", "Days")}
                        {th("total_duration", "Total inside")}
                        {th("avg_duration", "Avg daily")}
                        {th("avg_arrival", "Avg first-in")}
                        {th("avg_exit", "Avg last-out")}
                        <th>Incomplete</th><th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.items.map((r, i) => (
                        <tr key={r.employee_id}>
                          {isAdmin && <td><input type="checkbox" checked={selected.has(r.employee_id)}
                            onChange={() => toggle(r.employee_id)} /></td>}
                          <td>{((p?.page ?? 1) - 1) * (p?.page_size ?? 25) + i + 1}</td>
                          <td><Link to={`/employees/${encodeURIComponent(r.employee_id)}`}>{r.employee_name || "—"}</Link></td>
                          <td><Link to={`/employees/${encodeURIComponent(r.employee_id)}`}>{r.employee_id}</Link></td>
                          <td>{r.department}</td>
                          <td style={{ textTransform: "capitalize" }}>{r.location}</td>
                          <td>{r.policy_group_name
                            ? <span className="tag ok">{r.policy_group_name}</span>
                            : <span className="notice">—</span>}</td>
                          <td>{r.present_days}</td>
                          <td>{r.total_inside_hhmm}</td>
                          <td>{secsToHHMM(r.avg_daily_seconds)}</td>
                          <td>{r.avg_arrival ?? "—"}</td>
                          <td>{r.avg_exit ?? "—"}</td>
                          <td>{r.incomplete_days || ""}</td>
                          <td><Link to={`/employees/${encodeURIComponent(r.employee_id)}`}>View →</Link></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="pager">
                  <button className="ghost" disabled={(p?.page ?? 1) <= 1} onClick={() => setPage(page - 1)}>Prev</button>
                  <span className="notice">Page {p?.page} / {p?.total_pages}</span>
                  <button className="ghost" disabled={(p?.page ?? 1) >= (p?.total_pages ?? 1)} onClick={() => setPage(page + 1)}>Next</button>
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
